/**
 * 撤销重做历史类型。
 */
export type UndoRedoHistoryKind =
    | 'model'
    | 'view'
    | 'in-command'
    | 'preview-session';

/**
 * 目标历史栈。
 */
export type UndoRedoTargetHistoryStack =
    | 'model'
    | 'view'
    | 'in-command'
    | 'none';

/**
 * 事务目标。
 */
export type UndoRedoTransactionTarget =
    | 'model'
    | 'view'
    | 'none';

/**
 * redo 栈清理目标。
 */
export type UndoRedoClearRedoTarget =
    | 'model'
    | 'view'
    | 'none';

/**
 * 提交边界。
 */
export type UndoRedoCommitBoundary =
    | 'immediate'
    | 'finish-drawing'
    | 'mouseup'
    | 'ok'
    | 'enter'
    | 'finish-command'
    | 'not-applicable';

/**
 * 取消边界。
 */
export type UndoRedoCancelBoundary =
    | 'esc'
    | 'cancel'
    | 'rollback-preview'
    | 'not-applicable';

/**
 * SolidWorks Previous View 可回退的最大视图变化次数。
 */
export const SOLIDWORKS_PREVIOUS_VIEW_HISTORY_LIMIT = 10;

/**
 * 撤销重做行为规范。
 */
export interface UndoRedoBehaviorSpec {
    /** 场景稳定 ID。 */
    readonly scenarioId: string;
    /** 场景标题。 */
    readonly title: string;
    /** SolidWorks 期望行为。 */
    readonly solidWorksExpectedBehavior: string;
    /** PuppyCAD 当前入口或目标入口。 */
    readonly puppyCadEntry: string;
    /** 历史类型。 */
    readonly historyKind: UndoRedoHistoryKind;
    /** 最终进入的目标历史栈。 */
    readonly targetHistoryStack: UndoRedoTargetHistoryStack;
    /** 事务目标。 */
    readonly transactionTarget: UndoRedoTransactionTarget;
    /** redo 清理目标。 */
    readonly clearsRedoStack: UndoRedoClearRedoTarget;
    /** 提交边界。 */
    readonly commitBoundary: UndoRedoCommitBoundary;
    /** 取消边界。 */
    readonly cancelBoundary: UndoRedoCancelBoundary;
    /** 可选历史数量上限；当前用于 Previous View 行为基线。 */
    readonly historyLimit?: number;
    /** 是否必须测试。 */
    readonly requiresTest: boolean;
    /** 设计备注。 */
    readonly notes: string;
}

/**
 * SolidWorks 对齐的 Phase 0 撤销重做行为基线。
 */
export const UNDO_REDO_BEHAVIOR_SPECS = [
    {
        scenarioId: 'sketch-line-create',
        title: '创建草图线段',
        solidWorksExpectedBehavior: '完成实体创建后成为最近模型修改，可通过 Ctrl+Z 撤销。',
        puppyCadEntry: 'useEditorSketchWorkflow.handleDrawingFinish -> Viewer.finishCurrentDrawing',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'finish-drawing',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '连续绘制过程中的点级撤销不属于本场景。',
    },
    {
        scenarioId: 'sketch-line-undo-last-point',
        title: '连续绘制草图线段时撤销上一个点',
        solidWorksExpectedBehavior: '撤销当前绘制命令中的上一个点，不撤销已提交模型操作。',
        puppyCadEntry: 'EditorToolbar.vue @undo-point -> Viewer.undoSketchDrawingPoint',
        historyKind: 'in-command',
        targetHistoryStack: 'in-command',
        transactionTarget: 'none',
        clearsRedoStack: 'none',
        commitBoundary: 'not-applicable',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '该场景不能复用全局模型 undo。',
    },
    {
        scenarioId: 'model-undo-after-sketch-finish',
        title: '完成草图后 Ctrl+Z',
        solidWorksExpectedBehavior: '撤销最近提交的草图模型修改。',
        puppyCadEntry: '待新增 ModelHistoryManager 全局入口',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'none',
        clearsRedoStack: 'none',
        commitBoundary: 'not-applicable',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '执行已有 transaction 的 before，不创建新 transaction。',
    },
    {
        scenarioId: 'sketch-selection-delete',
        title: '删除草图实体与智能尺寸选择',
        solidWorksExpectedBehavior: '删除完整选择集是一个模型修改，可撤销和重做。',
        puppyCadEntry: 'useEditorSketchWorkflow.handleKeyDown(Delete) -> Viewer.deleteCurrentSketchSelection',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '实体、显式尺寸和引用被删实体或控制点的关联尺寸必须作为同一事务删除。',
    },
    {
        scenarioId: 'sketch-point-drag',
        title: '拖拽草图点',
        solidWorksExpectedBehavior: '拖拽过程不逐帧入栈，鼠标释放后一次性提交。',
        puppyCadEntry: 'PointDragStrategy -> Viewer.previewSketchParametricPointDrag/PointTargets -> SketchParametricSolveCoordinator',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'mouseup',
        cancelBoundary: 'esc',
        requiresTest: true,
        notes: 'pointermove 只更新预览或 live state，mouseup 才提交。',
    },
    {
        scenarioId: 'sketch-coincident-constraint-add',
        title: '添加重合约束',
        solidWorksExpectedBehavior: '约束添加与求解影响一起成为模型修改。',
        puppyCadEntry: 'Viewer.sketchRelationCreationService -> SketchRelationCreationService.solveActiveSketchParametricRelations -> Viewer.solveActiveSketchParametricState -> SketchParametricSolveCoordinator',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: 'Phase 2 必须记录 solver island。',
    },
    {
        scenarioId: 'sketch-dimension-add',
        title: '添加尺寸约束',
        solidWorksExpectedBehavior: '尺寸是约束和参数，添加后可撤销。',
        puppyCadEntry: 'SketchDimensionApplicationService.createDimension -> SketchDimensionRepository + SketchDimensionHistoryAdapter',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '尺寸持久 ID 为 cad:sketch-dimension:<uuid>；dimension patch 与 solver patch 同一事务。',
    },
    {
        scenarioId: 'sketch-dimension-value-edit',
        title: '修改尺寸值',
        solidWorksExpectedBehavior: '尺寸值变化是模型修改，影响求解结果。',
        puppyCadEntry: 'SketchDimensionApplicationService.changeNominalValue（Modify 确认）',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'ok',
        cancelBoundary: 'cancel',
        requiresTest: true,
        notes: 'Phase 2 记录 driving / driven 状态。',
    },
    {
        scenarioId: 'sketch-polygon-parameter-edit',
        title: '修改多边形参数',
        solidWorksExpectedBehavior: '边数、圆类型、半径或角度修改后，多边形几何与关联关系作为同一步模型修改撤销。',
        puppyCadEntry: 'Viewer.rebuildPolygonDrawingCollectionByParameters -> Viewer.executeActiveSketchRuntimeHistoryTransaction',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '事务类型为 sketch.property.polygon.parameters，包含种子线、阵列实例、辅助圆和几何关系。',
    },
    {
        scenarioId: 'feature-extrude-depth-edit',
        title: '编辑拉伸距离',
        solidWorksExpectedBehavior: 'PropertyManager OK 接受参数修改，Cancel 放弃。',
        puppyCadEntry: 'Editor.vue handlePropertyManagerConfirm -> FeatureEditOrchestrator.replaceFeatureCommand',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'ok',
        cancelBoundary: 'cancel',
        requiresTest: true,
        notes: 'worker replay 只用于派生几何重建。',
    },
    {
        scenarioId: 'feature-delete',
        title: '删除 feature',
        solidWorksExpectedBehavior: '删除 feature 是模型修改，下游依赖需要 dirty rebuild。',
        puppyCadEntry: 'FeatureTree.vue -> featureTreeStore.handleFeatureDelete',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: 'Phase 1 后续扩展。',
    },
    {
        scenarioId: 'feature-suppress-toggle',
        title: '抑制 / 解除抑制 feature',
        solidWorksExpectedBehavior: '抑制状态是模型修改，影响重建与 feature tree。',
        puppyCadEntry: '待新增 feature suppress domain',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '需要先建立 feature suppression 主状态。',
    },
    {
        scenarioId: 'view-rotate',
        title: '旋转视图',
        solidWorksExpectedBehavior: '视图变化进入 Previous View，不影响模型 undo。',
        puppyCadEntry: 'CameraManager.controls',
        historyKind: 'view',
        targetHistoryStack: 'view',
        transactionTarget: 'view',
        clearsRedoStack: 'view',
        commitBoundary: 'mouseup',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '不得清空模型 redo stack。',
    },
    {
        scenarioId: 'view-pan',
        title: '平移视图',
        solidWorksExpectedBehavior: '视图变化进入 view history，不影响模型历史。',
        puppyCadEntry: 'CameraManager.MouseMode.Pan / Ctrl+右键平移',
        historyKind: 'view',
        targetHistoryStack: 'view',
        transactionTarget: 'view',
        clearsRedoStack: 'view',
        commitBoundary: 'mouseup',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '不得生成 model transaction。',
    },
    {
        scenarioId: 'view-zoom',
        title: '缩放视图',
        solidWorksExpectedBehavior: '缩放是视图变化，进入 view history，不影响模型 undo。',
        puppyCadEntry: 'CameraManager.controls wheel / dolly',
        historyKind: 'view',
        targetHistoryStack: 'view',
        transactionTarget: 'view',
        clearsRedoStack: 'view',
        commitBoundary: 'mouseup',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '滚轮或缩放手势应合并为一次视图快照边界。',
    },
    {
        scenarioId: 'view-fit',
        title: 'Fit View',
        solidWorksExpectedBehavior: 'Fit View 是视图变化，进入 view history，不影响模型历史。',
        puppyCadEntry: 'CameraManager 后续 fit/focus 入口',
        historyKind: 'view',
        targetHistoryStack: 'view',
        transactionTarget: 'view',
        clearsRedoStack: 'view',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: 'Fit View 不应触发任何模型 transaction。',
    },
    {
        scenarioId: 'view-previous',
        title: 'Previous View',
        solidWorksExpectedBehavior: 'Previous View 回退最近视图变化，最多保留 10 次视图变化。',
        puppyCadEntry: '待新增 ViewHistoryManager.previousView',
        historyKind: 'view',
        targetHistoryStack: 'view',
        transactionTarget: 'none',
        clearsRedoStack: 'none',
        commitBoundary: 'not-applicable',
        cancelBoundary: 'not-applicable',
        historyLimit: SOLIDWORKS_PREVIOUS_VIEW_HISTORY_LIMIT,
        requiresTest: true,
        notes: 'Previous View 消费 view history，不创建新的模型事务。',
    },
    {
        scenarioId: 'assembly-component-move',
        title: '移动装配组件',
        solidWorksExpectedBehavior: '组件位姿 commit 后是模型状态，可撤销。',
        puppyCadEntry: 'useEditorAssemblyWorkflow.confirmAssemblyTransformCommand -> MoveComponentController.commit',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'finish-command',
        cancelBoundary: 'cancel',
        requiresTest: true,
        notes: 'Phase 4 先覆盖 transform，再接 Mate。',
    },
    {
        scenarioId: 'assembly-component-fix-float',
        title: '固定 / 浮动组件',
        solidWorksExpectedBehavior: '固定状态是模型状态，可撤销。',
        puppyCadEntry: 'AssemblyComponentFixationService.writeSceneObjectFixationState',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: 'Phase 4 纳入 AssemblyHistoryAdapter。',
    },
    {
        scenarioId: 'assembly-mate-add',
        title: '未来添加 mate',
        solidWorksExpectedBehavior: 'Mate 是装配约束，solver island 位姿必须可撤销。',
        puppyCadEntry: '当前无 Mate / MateSolver',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'ok',
        cancelBoundary: 'cancel',
        requiresTest: true,
        notes: '必须晚于 ComponentInstance 和 transform history。',
    },
    {
        scenarioId: 'property-manager-ok',
        title: 'PropertyManager 中 OK',
        solidWorksExpectedBehavior: '接受、执行命令并关闭 PropertyManager。',
        puppyCadEntry: 'Editor.vue handlePropertyManagerConfirm',
        historyKind: 'preview-session',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'ok',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: '若没有模型变化，应丢弃空事务。',
    },
    {
        scenarioId: 'property-manager-cancel',
        title: 'PropertyManager 中 Cancel',
        solidWorksExpectedBehavior: '忽略当前编辑，恢复 preview 前状态。',
        puppyCadEntry: 'Editor.vue handlePropertyManagerCancel',
        historyKind: 'preview-session',
        targetHistoryStack: 'none',
        transactionTarget: 'none',
        clearsRedoStack: 'none',
        commitBoundary: 'not-applicable',
        cancelBoundary: 'cancel',
        requiresTest: true,
        notes: 'Cancel 不生成历史，也不清空 redo。',
    },
    {
        scenarioId: 'model-new-operation-after-undo',
        title: 'undo 后执行新操作',
        solidWorksExpectedBehavior: '新模型修改必须清空 redo stack。',
        puppyCadEntry: '待新增 ModelHistoryManager.commit',
        historyKind: 'model',
        targetHistoryStack: 'model',
        transactionTarget: 'model',
        clearsRedoStack: 'model',
        commitBoundary: 'immediate',
        cancelBoundary: 'not-applicable',
        requiresTest: true,
        notes: 'Phase 1 必须覆盖。',
    },
    {
        scenarioId: 'operation-failed',
        title: '操作失败',
        solidWorksExpectedBehavior: '失败操作不进入历史，preview / worker 临时结果回滚。',
        puppyCadEntry: 'FeaturePreviewRequestQueue / Worker command execution',
        historyKind: 'preview-session',
        targetHistoryStack: 'none',
        transactionTarget: 'none',
        clearsRedoStack: 'none',
        commitBoundary: 'not-applicable',
        cancelBoundary: 'rollback-preview',
        requiresTest: true,
        notes: '失败不得污染主状态。',
    },
] as const satisfies readonly UndoRedoBehaviorSpec[];

/**
 * 按历史类型查询行为规范。
 *
 * @param historyKind 历史类型。
 * @returns 匹配的行为规范列表。
 */
export function listUndoRedoBehaviorSpecsByHistoryKind(
    historyKind: UndoRedoHistoryKind,
): readonly UndoRedoBehaviorSpec[] {
    return UNDO_REDO_BEHAVIOR_SPECS.filter((spec) => spec.historyKind === historyKind);
}

/**
 * 按场景 ID 查询行为规范。
 *
 * @param scenarioId 场景 ID。
 * @returns 命中的行为规范；不存在时返回 `undefined`。
 */
export function getUndoRedoBehaviorSpecByScenarioId(
    scenarioId: string,
): UndoRedoBehaviorSpec | undefined {
    return UNDO_REDO_BEHAVIOR_SPECS.find((spec) => spec.scenarioId === scenarioId);
}
